// Cart price check (flag REACT_APP_CART_PRICE_CHECK_ENABLED; Nichlas 12/9:
// "the moment something is put in the cart we check its price, without it
// costing performance or speed"). Pure module - no flag reads here, so
// enviroment.ts can import the default poll schedule without a cycle. The
// I/O and the module state live in hooks/useCartPriceCheck.ts.

/** Fields of a price row the cart compares (ProductPrice and PriceDto both carry them). */
export interface PriceRowFields {
  minQuantity: number;
  adjustedPrice: number;
  adjustedUnitPrice: number;
  adjustedCustomerPrice?: number;
}

/** The states the API's status GET answers (cartpricecheck:result:{serial}, written by its worker). */
export type CartPriceCheckState =
  | "queued"
  | "updated"
  | "unchanged"
  | "noPrices"
  | "shrunkKept"
  | "failed"
  | "none";

// The status poll after a 202 queued: the API's batch window is 3 s and a
// small supplier call answers in 1-3 s, so the first read at 3 s catches
// most checks, the second 8 s later the slow ones, the third 15 s after that
// is the last attempt (chained delays, like the "Prices on their way" poll).
export const DEFAULT_CART_PRICE_CHECK_POLL_MS: readonly number[] = [
  3_000, 8_000, 15_000,
];

// Owner rule: at most 3 status calls per checked logo (parseHealDelaysMs
// caps the env override at the same 3).
export const CART_PRICE_CHECK_MAX_POLLS = 3;

// A design add fires one POST per distinct logo, never more than this many.
export const CART_PRICE_CHECK_MAX_LOGOS_PER_ADD = 10;

// The API's default per-serial cooldown (CartPriceCheckCooldownMinutes = 10):
// a second add of the same logo inside it costs nothing, the answer would be
// 202 cooldown anyway.
export const CART_PRICE_CHECK_DEFAULT_COOLDOWN_S = 600;

// Transport budget per call, same as the storage look-up on add.
export const CART_PRICE_CHECK_TIMEOUT_MS = 8_000;

// After transport trouble / a 5xx / the timeout: no add asks for a minute.
export const CART_PRICE_CHECK_TRANSPORT_PAUSE_MS = 60_000;

// After a 404 (the API flag is off - or the logo is outside the route's
// org): no add asks for five minutes, then ONE probe per window.
export const CART_PRICE_CHECK_UNAVAILABLE_PAUSE_MS = 5 * 60_000;

// 202 dailyCap without a usable retryAfterSeconds: pause an hour.
export const CART_PRICE_CHECK_DAILY_CAP_PAUSE_S = 3600;

// The first status poll never fires later than this, whatever the API's
// pollAfterSeconds says (its batch window is clamped to 1..30 s server
// side; this is the client's own ceiling against a misconfigured value).
export const CART_PRICE_CHECK_MAX_FIRST_POLL_MS = 60_000;

// An "updated" mark the sync never consumed (its re-read failed, or the
// cart entry's query is disabled) cannot turn into the note after this
// long: a later, unrelated price diff must not wear the check's note.
export const CART_PRICE_CHECK_UPDATED_TTL_MS = 2 * 60_000;

/**
 * The delay before status poll number `attempt` (0-based), or undefined
 * when the budget is spent. Pure so the plan is testable without timers.
 */
export function cartPriceCheckPollDelay(
  attempt: number,
  delays: readonly number[] = DEFAULT_CART_PRICE_CHECK_POLL_MS
): number | undefined {
  if (!Number.isInteger(attempt) || attempt < 0) return undefined;
  if (attempt >= CART_PRICE_CHECK_MAX_POLLS) return undefined;
  return delays[attempt];
}

/**
 * The logos an add asks for: ids > 0, deduped, minus the ones still held
 * (asked for within their cooldown - `heldUntilMs` maps logo id to the
 * instant the hold ends), capped at `cap`. Order = first occurrence.
 */
export function selectCartPriceCheckLogoIds(
  logoIds: readonly number[],
  heldUntilMs: ReadonlyMap<number, number>,
  nowMs: number,
  cap: number = CART_PRICE_CHECK_MAX_LOGOS_PER_ADD
): number[] {
  const ids: number[] = [];
  for (const id of logoIds) {
    if (ids.length >= cap) break;
    if (!(id > 0) || ids.includes(id)) continue;
    const until = heldUntilMs.get(id);
    if (until !== undefined && nowMs < until) continue;
    ids.push(id);
  }
  return ids;
}

/**
 * The delay before the FIRST status poll after a 202 queued: the poll
 * schedule's first entry, pushed back to the API's `pollAfterSeconds` (its
 * batch window - the check cannot have run before it closed) when that is
 * later, and never past CART_PRICE_CHECK_MAX_FIRST_POLL_MS. The later
 * ticks keep their chained delays, so the poll budget (<= 3 calls) is
 * unchanged - a wider window on the API costs no extra call here.
 */
export function cartPriceCheckFirstPollDelay(
  pollAfterSeconds: unknown,
  delays: readonly number[] = DEFAULT_CART_PRICE_CHECK_POLL_MS
): number {
  const first = cartPriceCheckPollDelay(0, delays) ?? DEFAULT_CART_PRICE_CHECK_POLL_MS[0];
  const apiMs = cartPriceCheckSeconds(pollAfterSeconds, 0) * 1000;
  return Math.min(Math.max(first, apiMs), CART_PRICE_CHECK_MAX_FIRST_POLL_MS);
}

/**
 * Whether a failed price-check POST is deterministic for the logo (a 4xx:
 * 404 = flag off / logo outside the route's org, 400 = no access to the
 * logo's org, ...) - asking again inside the session hold would only fail
 * the same way - as opposed to transport trouble / a 5xx, where the message
 * may never have reached the queue and the hold is released.
 */
export function isCartPriceCheckDeterministicError(code: unknown): boolean {
  return typeof code === "number" && code >= 400 && code < 500;
}

/** Whole seconds from an API field; anything but a positive finite number = the fallback. */
export function cartPriceCheckSeconds(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.ceil(value)
    : fallback;
}

/**
 * Whether two price-row lists differ in the fields the cart shows - the
 * exact rule useCartSync.isLogoStale has always applied to a logo line
 * (row count, then row by row: adjustedPrice / adjustedUnitPrice /
 * adjustedCustomerPrice / minQuantity), lifted out unchanged so the cart
 * price check can ask the same question of a design's total prices.
 */
export function priceFieldsDiffer(
  cartPrices: readonly PriceRowFields[] | undefined,
  freshPrices: readonly PriceRowFields[] | undefined
): boolean {
  const current = cartPrices || [];
  const fresh = freshPrices || [];
  if (current.length !== fresh.length) return true;
  return current.some((cartPrice, index) => {
    const freshPrice = fresh[index];
    return (
      !freshPrice ||
      cartPrice.adjustedPrice !== freshPrice.adjustedPrice ||
      cartPrice.adjustedUnitPrice !== freshPrice.adjustedUnitPrice ||
      cartPrice.adjustedCustomerPrice !== freshPrice.adjustedCustomerPrice ||
      cartPrice.minQuantity !== freshPrice.minQuantity
    );
  });
}
